/**
 * Utility functions for transforming and working with order data
 */

import type { GetOrdersQuery } from '../../../../generated';
import { DEFAULT_PAGE_SIZE } from '../../constants';
import { formatOrderDeliveryDate } from '../../utils';

import type {
  CustomProduct,
  OrderWithCustomProducts,
  FormattedOrdersResponse,
  SearchOrderQueryVariables,
  GetQueryVariablesParams,
} from './types';

export { DEFAULT_PAGE_SIZE };

/**
 * An order product as it arrives on the wire, before it is flattened.
 */
interface RawOrderProduct {
  product?: {
    id?: string | null;
    externalId?: string | null;
    unit?: string | null;
    name?: string | null;
  } | null;
  amount?: number | null;
}

/** A product entry we know carries a product to read. */
type RawOrderProductWithProduct = RawOrderProduct & {
  product: NonNullable<RawOrderProduct['product']>;
};

/**
 * An order as it arrives on the wire: every field of the UI order except the
 * flattened `products`, which this module derives from `orderProducts`.
 *
 * Note `createdAt` is milliseconds and `deliveryDate` is seconds. See
 * `../../utils/orderDisplay.ts` before touching either.
 */
export type RawOrder = Omit<OrderWithCustomProducts, 'products'> & {
  orderProducts?: ReadonlyArray<RawOrderProduct | null> | null;
};

const EMPTY_PAGE_INFO = {
  hasNextPage: false,
  hasPreviousPage: false,
  startCursor: null,
  endCursor: null,
};

/**
 * Type guard to check if an order product carries a product
 */
const isValidOrderProduct = (
  orderProduct?: RawOrderProduct | null
): orderProduct is RawOrderProductWithProduct =>
  orderProduct?.product !== undefined && orderProduct?.product !== null;

/**
 * Extract product information from an order product
 * Combines the product details with the amount from the order
 *
 * @param orderProduct - The order product fragment from GraphQL
 * @returns CustomProduct with flattened structure
 */
export const extractProductInfo = ({
  product,
  amount,
}: RawOrderProductWithProduct): CustomProduct => ({
  id: product.id || '',
  externalId: product.externalId ?? null,
  unit: product.unit ?? null,
  name: product.name || '',
  amount: amount ?? 0,
});

/**
 * Format a single order by transforming orderProducts array to products array
 * This flattens the nested structure for easier consumption in the UI
 *
 * @param orderData - Raw order data from GraphQL query
 * @returns Order with custom products structure
 */
export const formatOrder = (orderData: RawOrder): OrderWithCustomProducts => {
  const { orderProducts, ...rest } = orderData;

  const products = (orderProducts ?? [])
    .filter(isValidOrderProduct)
    .map(extractProductInfo);

  return {
    ...rest,
    products,
  };
};

/**
 * Format the orders search response from GraphQL
 * Extracts orders from connection edges and returns formatted data
 *
 * @param response - Raw response from GetOrders query
 * @returns Formatted orders with page info
 */
export const formatOrdersResponse = (
  response?: GetOrdersQuery
): FormattedOrdersResponse => {
  const searchOrder = response?.searchOrder;

  // `searchOrder` is a union: the other members are failure types with no edges.
  if (searchOrder?.__typename !== 'SearchOrderConnection') {
    return { orders: [], pageInfo: EMPTY_PAGE_INFO };
  }

  return {
    orders: searchOrder.edges.map(({ node }) => formatOrder(node)),
    pageInfo: searchOrder.pageInfo,
  };
};

/**
 * Build query variables for order search
 * Handles cursor-based pagination and search parameters
 *
 * @param params - Search parameters and pagination cursors
 * @returns GraphQL query variables
 */
export const getQueryVariables = ({
  searchTerm = '',
  supplierRefId = '',
  pageSize = DEFAULT_PAGE_SIZE,
  cursorBefore,
  cursorAfter,
}: GetQueryVariablesParams = {}): SearchOrderQueryVariables => {
  return {
    query: {
      keywords: searchTerm.length > 0 ? searchTerm : undefined,
      supplierRefId: supplierRefId.length > 0 ? supplierRefId : undefined,
    },
    after: cursorAfter,
    before: cursorBefore,
    first: pageSize,
  };
};

/**
 * Check if an order is a test order
 *
 * @param order - The order to check
 * @returns True if the order is marked as a test order
 */
export const isTestOrder = (order: OrderWithCustomProducts): boolean => {
  return order.isTest === true;
};

/**
 * Format order reference ID for display
 *
 * @param order - The order to format
 * @returns Formatted reference ID or fallback
 */
export const formatOrderReferenceId = (
  order: OrderWithCustomProducts
): string => {
  return order.referenceId || order.id;
};

/**
 * Get order display name (buyer display name or name)
 *
 * @param order - The order to get buyer name from
 * @returns Buyer display name or name
 */
export const getOrderBuyerDisplayName = (
  order: OrderWithCustomProducts
): string => {
  if (!order.buyer) return 'Unknown Buyer';
  return order.buyer.displayName || order.buyer.name || 'Unknown Buyer';
};

/**
 * Get order supplier name
 *
 * @param order - The order to get supplier name from
 * @returns Distributor name
 */
export const getOrderSupplierName = (
  order: OrderWithCustomProducts
): string => {
  return order.supplier?.name || 'Unknown Distributor';
};

/**
 * Format delivery date for display
 *
 * `deliveryDate` is epoch SECONDS, so this delegates to the shared formatter
 * rather than handing it to `new Date()`, which expects milliseconds.
 *
 * @param order - The order to format date from
 * @returns Formatted delivery date or fallback
 */
export const formatDeliveryDate = (order: OrderWithCustomProducts): string =>
  formatOrderDeliveryDate(order.deliveryDate);
